import type { EdgeRuntimeSnapshot } from "@sitescene/types";

import { edgeHealthSchema } from "@sitescene/shared";

import { applicationStatus, timestamp } from "./configuration-status";

export function ConfigurationStatus({
	desired,
	now,
	refreshFailed,
	reports,
}: {
	desired: null | string;
	now: number;
	refreshFailed: boolean;
	reports: EdgeRuntimeSnapshot[];
}) {
	return (
		<section
			aria-label="Edge Device reports"
			className="space-y-3 rounded-lg border border-secondary p-4"
		>
			<h3 className="font-semibold">Edge Device reports</h3>
			{!reports.length && (
				<p>
					No device report yet. A published configuration will be applied
					automatically during installation.
				</p>
			)}
			{refreshFailed && (
				<p className="text-warning-primary">
					Reports could not be refreshed. Current contact is unknown.
				</p>
			)}
			{reports.length > 1 && (
				<p>
					Multiple device identities have reported. Each report is shown
					separately; none is assumed to be the active device.
				</p>
			)}
			{reports.map((item) => {
				const time =
					timestamp(item.receivedAt) === "Unknown time"
						? Number.NaN
						: new Date(item.receivedAt).getTime();
				const age = now - time;
				// Runtime requests a 60-second check-in. This is a UI warning after three missed intervals, not a device-offline assertion.
				const unknown = refreshFailed || !Number.isFinite(time) || age < 0;
				const stale = !unknown && age > 180_000;
				return (
					<div
						className="space-y-1 border-t border-secondary pt-3 text-sm"
						key={item.credentialId}
					>
						<p className="font-medium">
							{applicationStatus(desired, item.report)}
						</p>
						<p>
							Device identity:{" "}
							<span className="font-mono break-all">{item.subjectId}</span>
						</p>
						<p>
							Last report: {timestamp(item.receivedAt)}
							{unknown
								? " · Contact freshness unknown"
								: stale
									? ` · Stale (${Math.floor(age / 60_000)} min ago)`
									: ""}
						</p>
						<p>
							Last reported outcome: {item.report.state}
							{item.report.errorCode ? ` (${item.report.errorCode})` : ""}
						</p>
						<dl className="grid gap-1 font-mono text-xs break-all">
							<dt>Published revision</dt>
							<dd>{desired ?? "None"}</dd>
							<dt>Attempted revision</dt>
							<dd>{item.report.attemptedRevision ?? "Not reported"}</dd>
							<dt>Applied revision</dt>
							<dd>{item.report.appliedRevision ?? "Not reported"}</dd>
						</dl>
						<HealthReport health={item.report.health} />
					</div>
				);
			})}
			<p className="text-sm text-secondary">
				Saving stages a draft. Publishing requests application by the Edge
				Device. Application and recent contact do not establish camera
				connectivity or video delivery.
			</p>
		</section>
	);
}

function HealthReport({ health }: { health: unknown }) {
	const parsed = edgeHealthSchema.safeParse(health);
	if (!parsed.success) return <p>Source health: not reported</p>;
	const value = parsed.data;
	return (
		<details className="pt-2">
			<summary>
				Device and source health · {timestamp(value.capturedAt)}
			</summary>
			<p>
				Device uptime:{" "}
				{value.device
					? `${Math.floor(value.device.uptimeSeconds / 60)} minutes`
					: "not reported"}
			</p>
			<p>
				Capacity:{" "}
				{value.capacity
					? `${value.capacity.currentStreamCount} / ${value.capacity.maxStreamCount} sources; ${value.capacity.segmentQueueDepth} segments queued`
					: "not reported"}
			</p>
			<p>Device severity: {value.severity?.overall ?? "not reported"}</p>
			{value.streams?.map((stream) => (
				<p className="break-all" key={stream.configId}>
					Source identity {stream.configId}:{" "}
					{stream.connected ? "connected" : "not connected"}. Device-reported
					delivery metric:{" "}
					{stream.deliverySuccessRate == null
						? "not reported"
						: `${(stream.deliverySuccessRate * 100).toFixed(1)}%`}
					; p95 latency:{" "}
					{stream.deliveryLatencyP95Ms == null
						? "not reported"
						: `${stream.deliveryLatencyP95Ms.toFixed(0)} ms`}
					.
				</p>
			))}
			<p>
				These are device measurements. Local-only operation may also report
				success; receiver-confirmed cloud delivery is not established here.
			</p>
		</details>
	);
}
